import type {
  CreateIngredientCategoryPayload,
  UpdateIngredientCategoryPayload,
} from "@/modules/inventory/domain/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventoryIngredientCategoriesApi,
  type IngredientCategoriesListParams,
} from "./ingredient-categories-client";

export const ingredientCategoriesKeys = {
  all: ["inventory", "ingredient-categories"] as const,
  list: (params: IngredientCategoriesListParams) =>
    ["inventory", "ingredient-categories", "list", params] as const,
  lookup: ["inventory", "ingredient-categories", "lookup"] as const,
  detail: (id: string) =>
    ["inventory", "ingredient-categories", "detail", id] as const,
};

export function useIngredientCategoriesQuery(
  params: IngredientCategoriesListParams = {},
) {
  return useQuery({
    queryKey: ingredientCategoriesKeys.list(params),
    queryFn: () => inventoryIngredientCategoriesApi.list(params),
  });
}

export function useIngredientCategoriesLookupQuery() {
  return useQuery({
    queryKey: ingredientCategoriesKeys.lookup,
    queryFn: () => inventoryIngredientCategoriesApi.lookup(),
  });
}

export function useIngredientCategoryQuery(id: string) {
  return useQuery({
    queryKey: ingredientCategoriesKeys.detail(id),
    queryFn: () => inventoryIngredientCategoriesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateIngredientCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIngredientCategoryPayload) =>
      inventoryIngredientCategoriesApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ingredientCategoriesKeys.all });
    },
  });
}

export function useUpdateIngredientCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateIngredientCategoryPayload) =>
      inventoryIngredientCategoriesApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ingredientCategoriesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ingredientCategoriesKeys.all });
    },
  });
}

export function useReorderIngredientCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      inventoryIngredientCategoriesApi.reorder(orderedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ingredientCategoriesKeys.all });
    },
  });
}

export function useDeactivateIngredientCategory() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) =>
      inventoryIngredientCategoriesApi.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ingredientCategoriesKeys.all });
      qc.invalidateQueries({ queryKey: ["inventory", "ingredients"] });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
